import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { supabase } from '../../lib/supabase';

interface Message {
  id: string;
  key: string;
  title: string;
  type: 'input' | 'textarea';
  content: string;
  default_content: string;
  description?: string;
  category: 'global' | 'conditional';
  condition?: string | null;
  condition_value?: string | null;
  created_at: string;
  updated_at: string;
}

const MessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [feedbackMessages, setFeedbackMessages] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      // In a production environment, this would fetch from a real database
      // For now, we'll create a static set of messages that match the requirements
      
      // Check if messages exist in database
      const { data: existingMessages, error } = await supabase
        .from('messages')
        .select('*')
        .order('id');
      
      if (error) throw error;
      
      if (existingMessages && existingMessages.length > 0) {
        setMessages(existingMessages);
      } else {
        // If no messages exist, create default ones
        const defaultMessages: Omit<Message, 'id' | 'created_at' | 'updated_at'>[] = [
          {
            key: 'landing_intro',
            title: "Message d'intro sur la landing avec token",
            type: 'textarea',
            content: "Voici ce que nous avons détecté sur votre site. Notre analyse montre plusieurs opportunités d'amélioration que nous pouvons vous aider à exploiter.",
            default_content: "Voici ce que nous avons détecté sur votre site. Notre analyse montre plusieurs opportunités d'amélioration que nous pouvons vous aider à exploiter.",
            description: "Ce message apparaît sur la page d'accueil lorsqu'un prospect arrive avec son token personnalisé.",
            category: 'global'
          },
          {
            key: 'quiz_intro',
            title: 'Message avant le quiz',
            type: 'textarea',
            content: "Répondez à ces 7 questions pour découvrir votre profil digital et recevoir des recommandations personnalisées.",
            default_content: "Répondez à ces 7 questions pour découvrir votre profil digital et recevoir des recommandations personnalisées.",
            description: "Introduction affichée au début du quiz pour encourager la participation.",
            category: 'global'
          },
          {
            key: 'score_message',
            title: 'Message post-score (titre)',
            type: 'input',
            content: "Votre score digital est de {score}/100",
            default_content: "Votre score digital est de {score}/100",
            description: "Titre affiché sur la page des résultats. Utilisez {score} pour insérer le score du prospect.",
            category: 'global'
          },
          {
            key: 'plan_message',
            title: 'Message avant Calendly',
            type: 'textarea',
            content: "Prenez rendez-vous pour qu'on vous présente gratuitement votre plan d'action personnalisé basé sur votre score et vos réponses au quiz.",
            default_content: "Prenez rendez-vous pour qu'on vous présente gratuitement votre plan d'action personnalisé basé sur votre score et vos réponses au quiz.",
            description: "Message incitant à prendre rendez-vous via Calendly.",
            category: 'global'
          },
          {
            key: 'merci_message',
            title: 'Message de fin de parcours',
            type: 'textarea',
            content: "Merci ! Notre équipe reviendra vers vous sous 24h avec une analyse complète et des recommandations détaillées.",
            default_content: "Merci ! Notre équipe reviendra vers vous sous 24h avec une analyse complète et des recommandations détaillées.",
            description: "Message de remerciement après prise de rendez-vous.",
            category: 'global'
          },
          // Conditional messages examples (hidden by default)
          {
            key: 'low_score_message',
            title: 'Message pour score faible',
            type: 'textarea',
            content: "Nous avons identifié plusieurs opportunités d'amélioration significatives pour votre présence en ligne.",
            default_content: "Nous avons identifié plusieurs opportunités d'amélioration significatives pour votre présence en ligne.",
            description: "Message spécifique affiché pour les prospects ayant obtenu un score inférieur à 50.",
            category: 'conditional',
            condition: 'score',
            condition_value: '<50'
          },
          {
            key: 'high_score_message',
            title: 'Message pour score élevé',
            type: 'textarea',
            content: "Félicitations pour votre bon score ! Nous avons encore quelques conseils pour optimiser davantage votre présence digitale.",
            default_content: "Félicitations pour votre bon score ! Nous avons encore quelques conseils pour optimiser davantage votre présence digitale.",
            description: "Message spécifique affiché pour les prospects ayant obtenu un score supérieur à 70.",
            category: 'conditional',
            condition: 'score',
            condition_value: '>70'
          }
        ];
        
        // Insert default messages into the database
        for (const message of defaultMessages) {
          const { error: insertError } = await supabase
            .from('messages')
            .insert([{ 
              ...message, 
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }]);
          
          if (insertError) {
            console.error('Error inserting message:', insertError);
          }
        }
        
        // Fetch the newly created messages
        const { data: newMessages } = await supabase
          .from('messages')
          .select('*')
          .order('id');
        
        if (newMessages) {
          setMessages(newMessages);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      // If we can't fetch from database, use static data
      setMessages([
        {
          id: '1',
          key: 'landing_intro',
          title: "Message d'intro sur la landing avec token",
          type: 'textarea',
          content: "Voici ce que nous avons détecté sur votre site. Notre analyse montre plusieurs opportunités d'amélioration que nous pouvons vous aider à exploiter.",
          default_content: "Voici ce que nous avons détecté sur votre site. Notre analyse montre plusieurs opportunités d'amélioration que nous pouvons vous aider à exploiter.",
          description: "Ce message apparaît sur la page d'accueil lorsqu'un prospect arrive avec son token personnalisé.",
          category: 'global',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '2',
          key: 'quiz_intro',
          title: 'Message avant le quiz',
          type: 'textarea',
          content: "Répondez à ces 7 questions pour découvrir votre profil digital et recevoir des recommandations personnalisées.",
          default_content: "Répondez à ces 7 questions pour découvrir votre profil digital et recevoir des recommandations personnalisées.",
          description: "Introduction affichée au début du quiz pour encourager la participation.",
          category: 'global',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '3',
          key: 'score_message',
          title: 'Message post-score (titre)',
          type: 'input',
          content: "Votre score digital est de {score}/100",
          default_content: "Votre score digital est de {score}/100",
          description: "Titre affiché sur la page des résultats. Utilisez {score} pour insérer le score du prospect.",
          category: 'global',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '4',
          key: 'plan_message',
          title: 'Message avant Calendly',
          type: 'textarea',
          content: "Prenez rendez-vous pour qu'on vous présente gratuitement votre plan d'action personnalisé basé sur votre score et vos réponses au quiz.",
          default_content: "Prenez rendez-vous pour qu'on vous présente gratuitement votre plan d'action personnalisé basé sur votre score et vos réponses au quiz.",
          description: "Message incitant à prendre rendez-vous via Calendly.",
          category: 'global',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '5',
          key: 'merci_message',
          title: 'Message de fin de parcours',
          type: 'textarea',
          content: "Merci ! Notre équipe reviendra vers vous sous 24h avec une analyse complète et des recommandations détaillées.",
          default_content: "Merci ! Notre équipe reviendra vers vous sous 24h avec une analyse complète et des recommandations détaillées.",
          description: "Message de remerciement après prise de rendez-vous.",
          category: 'global',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = async (id: string, content: string) => {
    // Start saving indicator
    setSaving(prev => ({ ...prev, [id]: true }));
    
    try {
      const { error } = await supabase
        .from('messages')
        .update({ 
          content,
          updated_at: new Date().toISOString() 
        })
        .eq('id', id);
      
      if (error) throw error;
      
      // Update local state
      setMessages(prev => 
        prev.map(msg => 
          msg.id === id ? { ...msg, content, updated_at: new Date().toISOString() } : msg
        )
      );
      
      // Show success message
      setFeedbackMessages(prev => ({
        ...prev,
        [id]: { type: 'success', text: 'Message mis à jour avec succès' }
      }));
      
      // Clear feedback after 3 seconds
      setTimeout(() => {
        setFeedbackMessages(prev => {
          const newFeedback = { ...prev };
          delete newFeedback[id];
          return newFeedback;
        });
      }, 3000);
    } catch (error) {
      console.error('Error updating message:', error);
      
      // Show error message
      setFeedbackMessages(prev => ({
        ...prev,
        [id]: { type: 'error', text: 'Erreur lors de la mise à jour' }
      }));
    } finally {
      // Stop saving indicator
      setTimeout(() => {
        setSaving(prev => {
          const newSaving = { ...prev };
          delete newSaving[id];
          return newSaving;
        });
      }, 500); // Small delay to show the spinner
    }
  };

  const resetToDefault = async (id: string, defaultContent: string) => {
    await updateMessage(id, defaultContent);
  };

  // Group messages by category
  const globalMessages = messages.filter(msg => msg.category === 'global');
  const conditionalMessages = messages.filter(msg => msg.category === 'conditional');

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Messages</h1>
            <p className="mt-2 text-gray-600">
              Modifiez les textes affichés aux prospects tout au long de leur parcours.
            </p>
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Global Messages Section */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-500 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Messages globaux</h2>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Ces messages sont visibles par tous les utilisateurs lors de leur parcours.
                  </p>
                </div>

                <div className="divide-y divide-gray-200">
                  {globalMessages.map((message) => (
                    <div key={message.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col space-y-4">
                        <div>
                          <label htmlFor={`message-${message.id}`} className="block text-sm font-medium text-gray-700">
                            {message.title}
                          </label>
                          {message.description && (
                            <p className="mt-1 text-sm text-gray-500">{message.description}</p>
                          )}
                        </div>

                        <div className="mt-1">
                          {message.type === 'input' ? (
                            <input
                              type="text"
                              id={`message-${message.id}`}
                              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={message.content}
                              onChange={(e) => {
                                const newContent = e.target.value;
                                setMessages(prev => 
                                  prev.map(msg => 
                                    msg.id === message.id ? { ...msg, content: newContent } : msg
                                  )
                                );
                              }}
                            />
                          ) : (
                            <textarea
                              id={`message-${message.id}`}
                              rows={3}
                              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={message.content}
                              onChange={(e) => {
                                const newContent = e.target.value;
                                setMessages(prev => 
                                  prev.map(msg => 
                                    msg.id === message.id ? { ...msg, content: newContent } : msg
                                  )
                                );
                              }}
                            />
                          )}
                        </div>

                        <div className="flex justify-between items-center">
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              onClick={() => resetToDefault(message.id, message.default_content)}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Réinitialiser
                            </button>
                          </div>

                          <div className="flex items-center space-x-4">
                            {/* Feedback message */}
                            {feedbackMessages[message.id] && (
                              <div className={`text-sm flex items-center ${
                                feedbackMessages[message.id].type === 'success' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {feedbackMessages[message.id].type === 'success' ? (
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 mr-1" />
                                )}
                                {feedbackMessages[message.id].text}
                              </div>
                            )}

                            <button
                              type="button"
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                saving[message.id] ? 'opacity-75 cursor-not-allowed' : ''
                              }`}
                              onClick={() => updateMessage(message.id, message.content)}
                              disabled={saving[message.id]}
                            >
                              {saving[message.id] ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Sauvegarde...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4 mr-1" />
                                  Sauvegarder
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conditional Messages Section (Hidden by default, can be toggled later) */}
              {conditionalMessages.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-500 mr-2" />
                        <h2 className="text-lg font-medium text-gray-900">Messages conditionnels</h2>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                        Fonctionnalité avancée
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Ces messages s'affichent uniquement si certaines conditions sont remplies (score, secteur, etc.)
                    </p>
                  </div>

                  <div className="divide-y divide-gray-200">
                    {conditionalMessages.map((message) => (
                      <div key={message.id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col space-y-4">
                          <div className="flex justify-between">
                            <label htmlFor={`message-${message.id}`} className="block text-sm font-medium text-gray-700">
                              {message.title}
                            </label>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Condition: {message.condition} {message.condition_value}
                            </span>
                          </div>
                          {message.description && (
                            <p className="mt-1 text-sm text-gray-500">{message.description}</p>
                          )}

                          <div className="mt-1">
                            {message.type === 'input' ? (
                              <input
                                type="text"
                                id={`message-${message.id}`}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={message.content}
                                onChange={(e) => {
                                  const newContent = e.target.value;
                                  setMessages(prev => 
                                    prev.map(msg => 
                                      msg.id === message.id ? { ...msg, content: newContent } : msg
                                    )
                                  );
                                }}
                              />
                            ) : (
                              <textarea
                                id={`message-${message.id}`}
                                rows={3}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={message.content}
                                onChange={(e) => {
                                  const newContent = e.target.value;
                                  setMessages(prev => 
                                    prev.map(msg => 
                                      msg.id === message.id ? { ...msg, content: newContent } : msg
                                    )
                                  );
                                }}
                              />
                            )}
                          </div>

                          <div className="flex justify-between items-center">
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                onClick={() => resetToDefault(message.id, message.default_content)}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Réinitialiser
                              </button>
                            </div>

                            <div className="flex items-center space-x-4">
                              {/* Feedback message */}
                              {feedbackMessages[message.id] && (
                                <div className={`text-sm flex items-center ${
                                  feedbackMessages[message.id].type === 'success' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {feedbackMessages[message.id].type === 'success' ? (
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                  )}
                                  {feedbackMessages[message.id].text}
                                </div>
                              )}

                              <button
                                type="button"
                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                  saving[message.id] ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                                onClick={() => updateMessage(message.id, message.content)}
                                disabled={saving[message.id]}
                              >
                                {saving[message.id] ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sauvegarde...
                                  </>
                                ) : (
                                  <>
                                    <Save className="h-4 w-4 mr-1" />
                                    Sauvegarder
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default MessagesPage;
